import { formatDistanceToNowStrict } from 'date-fns';

import { GEMINI_REASONING_EFFORT_OPTIONS, effectiveGeminiReasoningEffort } from './config';

export const computeUniversityErrors = (form = {}) => {
  const errors = {};
  const name = form.name?.toString().trim();
  const code = form.code?.toString().trim();
  const slug = form.slug?.toString().trim();
  const seatCapacity = form.seatCapacity;
  const contactEmail = form.contactEmail?.toString().trim();

  if (!name) {
    errors.name = 'Name is required';
  }
  if (!code) {
    errors.code = 'Code is required';
  } else if (code.length < 2) {
    errors.code = 'Code must be at least 2 characters';
  }
  if (slug && !/^[a-z0-9-]+$/i.test(slug)) {
    errors.slug = 'Slug can only include letters, numbers, and hyphens';
  }

  if (seatCapacity !== undefined && seatCapacity !== null && seatCapacity !== '') {
    const numeric = Number(seatCapacity);
    if (Number.isNaN(numeric) || numeric < 0) {
      errors.seatCapacity = 'Seat capacity must be zero or a positive number';
    }
  }

  if (contactEmail && !/^\S+@\S+\.\S+$/.test(contactEmail)) {
    errors.contactEmail = 'Enter a valid email address';
  }

  return errors;
};

export const formatCurrency = (amountCents, currency = 'GBP') => {
  if (amountCents === undefined || amountCents === null) {
    return '?';
  }
  const normalisedCurrency = (currency || 'GBP').toString().toUpperCase();
  const amount = Number(amountCents) / 100;
  if (!Number.isFinite(amount)) {
    return '?';
  }
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: normalisedCurrency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${normalisedCurrency} ${amount.toFixed(2)}`;
  }
};

export const formatTokenBalance = (tokens) => {
  const value = Number(tokens);
  if (!Number.isFinite(value)) {
    return '0';
  }
  return value.toLocaleString();
};

export const formatInteger = (value) => {
  if (value === undefined || value === null) {
    return '?';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '?';
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(numeric);
};

export const formatRelativeTime = (value, fallback = '?') => {
  if (!value) {
    return fallback;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  return `${formatDistanceToNowStrict(date)} ago`;
};

export const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  const precision = size >= 10 || index === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[index]}`;
};

const ADMIN_EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export const isValidAdminEmail = (value) => ADMIN_EMAIL_PATTERN.test((value || '').toString().trim().toLowerCase());

export const formatInviteExpiry = (value) => {
  if (!value) {
    return '?';
  }

  const expiryDate = new Date(value);
  if (Number.isNaN(expiryDate.getTime())) {
    return '?';
  }

  const diff = formatDistanceToNowStrict(expiryDate);
  return expiryDate.getTime() >= Date.now() ? `${diff} remaining` : `${diff} ago`;
};

export const isAzureResponsesEndpoint = (value = '') => {
  const normalized = value.toString().trim();
  if (!normalized) {
    return false;
  }
  return /\/openai\/responses\/?(?:\?|$)/i.test(normalized)
    || /\/openai\/deployments\/[^/]+\/responses\/?(?:\?|$)/i.test(normalized);
};

export const hasAzureApiVersion = (value = '') => /[?&]api-version=/i.test(value);

export const isAzureMarkingConfigured = ({ endpoint, deploymentId, apiVersion }) => {
  const trimmedEndpoint = (endpoint || '').toString().trim();
  if (!trimmedEndpoint) {
    return false;
  }
  if (isAzureResponsesEndpoint(trimmedEndpoint)) {
    return hasAzureApiVersion(trimmedEndpoint) || Boolean((apiVersion || '').toString().trim());
  }
  return Boolean(trimmedEndpoint)
    && Boolean((deploymentId || '').toString().trim())
    && Boolean((apiVersion || '').toString().trim());
};

export const isAzureTranscriptionConfigured = ({ endpoint, deploymentId, apiVersion }) => {
  const trimmedEndpoint = (endpoint || '').toString().trim();
  if (!trimmedEndpoint) {
    return false;
  }

  const isFullEndpoint = /\/openai\/(?:deployments\/[^/]+\/)?audio\/transcriptions\/?(?:\?|$)/i.test(trimmedEndpoint);
  if (isFullEndpoint) {
    return hasAzureApiVersion(trimmedEndpoint) || Boolean((apiVersion || '').toString().trim());
  }

  return Boolean((deploymentId || '').toString().trim())
    && Boolean((apiVersion || '').toString().trim());
};

export const normalizeGeminiReasoningEffort = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return GEMINI_REASONING_EFFORT_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : 'off';
};

export const resolveGeminiReasoningEffort = (realtime = {}) => {
  if (realtime.reasoningEffort) {
    return normalizeGeminiReasoningEffort(realtime.reasoningEffort);
  }
  return realtime.thinkingEnabled
    ? normalizeGeminiReasoningEffort(realtime.thinkingLevel || 'minimal')
    : 'off';
};

export const buildGeminiThinkingPayload = (realtime = {}) => {
  const effort = effectiveGeminiReasoningEffort(
    realtime.realtimeModel,
    normalizeGeminiReasoningEffort(realtime.reasoningEffort),
  );
  return {
    reasoningEffort: effort,
    thinkingEnabled: effort !== 'off',
    thinkingLevel: effort === 'off' ? 'minimal' : effort,
  };
};

export const mapContextWindowCompressionToForm = (config = {}) => ({
  contextWindowCompressionEnabled: config.enabled !== false,
  contextWindowCompressionTriggerTokens: config.triggerTokens === null || config.triggerTokens === undefined
    ? ''
    : String(config.triggerTokens),
  contextWindowCompressionTargetTokens: config.targetTokens === null || config.targetTokens === undefined
    ? ''
    : String(config.targetTokens),
});

export const parseOptionalTokenLimit = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(1, Math.floor(numeric)) : null;
};

export const buildContextWindowCompressionPayload = (realtime = {}) => ({
  enabled: realtime.contextWindowCompressionEnabled !== false,
  triggerTokens: parseOptionalTokenLimit(realtime.contextWindowCompressionTriggerTokens),
  targetTokens: parseOptionalTokenLimit(realtime.contextWindowCompressionTargetTokens),
});

export const isEditableElement = (element) => {
  if (!element || typeof element !== 'object') {
    return false;
  }
  const tagName = element.tagName?.toLowerCase();
  if (tagName && ['input', 'textarea', 'select'].includes(tagName)) {
    return true;
  }
  if (element.isContentEditable) {
    return true;
  }
  const role = typeof element.getAttribute === 'function' ? element.getAttribute('role') : null;
  if (role && ['textbox', 'combobox'].includes(role)) {
    return true;
  }
  const hasPopup = typeof element.getAttribute === 'function' ? element.getAttribute('aria-haspopup') : null;
  if (hasPopup === 'listbox') {
    return true;
  }
  if (typeof element.closest === 'function') {
    if (element.closest('input, textarea, select, [contenteditable="true"]')) {
      return true;
    }
    if (element.closest('[role="textbox"], [role="combobox"], [aria-haspopup="listbox"]')) {
      return true;
    }
  }
  return false;
};

export const mapFeedbackSeverity = (type) => {
  switch (type) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'success';
  }
};
